import type React from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    RiBillLine,
    RiBillFill,
    RiCoupon2Line,
    RiCoupon2Fill,
} from "react-icons/ri";
import { useIsMobile } from "../../hooks/useIsMobile";
import { TbLayoutDashboard, TbLayoutDashboardFilled } from "react-icons/tb";
import { PiUsers } from "react-icons/pi";
import { LiaUsersCogSolid } from "react-icons/lia";
import {
    BsChevronUp,
    BsChevronDown,
    BsFileBarGraph,
    BsFileBarGraphFill,
    BsFillMegaphoneFill,
    BsMegaphone,
} from "react-icons/bs";
import {
    HiMiniWrenchScrewdriver,
    HiOutlineWrenchScrewdriver,
} from "react-icons/hi2";
import { IoSyncCircleOutline, IoSyncCircleSharp } from "react-icons/io5";
import {
    MdEventRepeat,
    MdOutlineEventRepeat,
    MdOutlinePalette,
    MdPalette,
    MdOutlineCorporateFare,
    MdCorporateFare,
    MdErrorOutline,
    MdError,
    MdOutlineMarkEmailRead,
    MdMarkEmailRead,
    MdOutlineSettings,
    MdSettings,
    MdMonitorHeart,
    MdOutlineMonitorHeart,
} from "react-icons/md";
import { AiOutlineDatabase, AiFillDatabase } from "react-icons/ai";
import {
    TbAlertTriangle,
    TbBuildingWarehouse,
    TbPlugConnectedX,
} from "react-icons/tb";
import type { IconType } from "react-icons";
import { User } from "../../types/user";

interface AdminSideBarProps {
    isAdmin: boolean;
    user: User | null;
}

interface MenuChild {
    name: string;
    path: string;
    icon: IconType;
    filledIcon: IconType;
    permissions: string[];
}

interface MenuItem {
    name: string;
    path?: string;
    icon: IconType;
    filledIcon: IconType;
    permissions: string[];
    children?: MenuChild[];
}

const AdminSideBar: React.FC<AdminSideBarProps> = ({isAdmin, user}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [dragState, setDragState] = useState({
        isDragging: false,
        startY: 0,
        currentY: 0,
        dragDistance: 0,
    });
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile(900);

    const menuItems: MenuItem[] = [
        {
            name: "Dashboard",
            path: "/admin",
            icon: TbLayoutDashboard,
            filledIcon: TbLayoutDashboardFilled,
            permissions: ["ADMIN", "CREATOR"],
        },
        {
            name: "Manage Users",
            path: "/admin/manage-users",
            icon: PiUsers,
            filledIcon: LiaUsersCogSolid,
            permissions: ["ADMIN", "CREATOR"],
        },
        {
            name: "Manage Plans",
            path: "/admin/manage-plans",
            icon: RiBillLine,
            filledIcon: RiBillFill,
            permissions: ["ADMIN"],
        },
        {
            name: "Statistics",
            path: "/admin/statistics",
            icon: BsFileBarGraph,
            filledIcon: BsFileBarGraphFill,
            permissions: ["ADMIN"],
        },
        {
            name: "Maintenance Mode",
            path: "/admin/maintenance",
            icon: HiOutlineWrenchScrewdriver,
            filledIcon: HiMiniWrenchScrewdriver,
            permissions: ["ADMIN"],
        },
        {
            name: "Announcements",
            path: "/admin/announcements",
            icon: BsMegaphone,
            filledIcon: BsFillMegaphoneFill,
            permissions: ["ADMIN"],
        },
        {
            name: "Free Trial",
            path: "/admin/free-trial",
            icon: RiCoupon2Line,
            filledIcon: RiCoupon2Fill,
            permissions: ["ADMIN"],
        },
        {
            name: "Campaign Renewals",
            path: "/admin/campaign-renewals",
            icon: MdOutlineEventRepeat,
            filledIcon: MdEventRepeat,
            permissions: ["ADMIN"],
        },
        {
            name: "Failed Posts",
            path: "/admin/failed-posts",
            icon: MdErrorOutline,
            filledIcon: MdError,
            permissions: ["ADMIN"],
        },
        {
            name: "Weekly Summary",
            path: "/admin/weekly-summary",
            icon: MdOutlineMarkEmailRead,
            filledIcon: MdMarkEmailRead,
            permissions: ["ADMIN"],
        },
        {
            name: "App Config",
            path: "/admin/app-config",
            icon: MdOutlineSettings,
            filledIcon: MdSettings,
            permissions: ["ADMIN"],
        },
        {
            name: "Account Health",
            path: "/admin/account-health",
            icon: MdOutlineMonitorHeart,
            filledIcon: MdMonitorHeart,
            permissions: ["ADMIN"],
        },
        {
            name: "MLS Listing Sync",
            path: "/admin/listing-sync",
            icon: IoSyncCircleOutline,
            filledIcon: IoSyncCircleSharp,
            permissions: ["ADMIN"],
        },
        {
            name: "User Quota",
            path: "/admin/user-quota",
            icon: AiOutlineDatabase,
            filledIcon: AiFillDatabase,
            permissions: ["ADMIN"],
        },
        {
            name: "Caption Violations",
            path: "/admin/caption-violations",
            // Same glyph for both states — the active row already has its own
            // background, so the icon must not change shape on selection.
            icon: TbAlertTriangle,
            filledIcon: TbAlertTriangle,
            permissions: ["ADMIN"],
        },
        {
            name: "Commercial Video",
            path: "/admin/commercial-auto-video",
            // Same glyph for both states, for the reason given above.
            icon: TbBuildingWarehouse,
            filledIcon: TbBuildingWarehouse,
            permissions: ["ADMIN"],
        },
        {
            name: "Social Connections",
            path: "/admin/social-connections",
            // Same glyph for both states — the active row already has its own
            // background, so the icon must not change shape on selection.
            icon: TbPlugConnectedX,
            filledIcon: TbPlugConnectedX,
            permissions: ["ADMIN"],
        },
        {
            name: "Masters",
            icon: MdOutlineCorporateFare,
            filledIcon: MdCorporateFare,
            permissions: ["ADMIN"],
            children: [
                {
                    name: "Broker Branding",
                    path: "/admin/masters/brokerages",
                    icon: MdOutlinePalette,
                    filledIcon: MdPalette,
                    permissions: ["ADMIN"],
                },
            ],
        },
    ];

    // Helper to get user role for permission check
    const getUserRoles = (user: User | null) => {
        const roles = [];
        if (isAdmin === true) roles.push("ADMIN");
        if (user?.adminRole === "CREATOR") roles.push("CREATOR");
        return roles;
    };

    const userRoles = getUserRoles(user);

    const filteredMenuItems = menuItems
        .filter(item => item.permissions.some(role => userRoles.includes(role)))
        .map(item =>
            item.children
                ? {
                      ...item,
                      children: item.children.filter(child =>
                          child.permissions.some(role =>
                              userRoles.includes(role),
                          ),
                      ),
                  }
                : item,
        )
        .filter(item => !item.children || item.children.length > 0);

    const [expandedMenus, setExpandedMenus] = useState<string[]>(() =>
        menuItems
            .filter(item =>
                item.children?.some(child => child.path === location.pathname),
            )
            .map(item => item.name),
    );

    // Keep a parent open whenever the active route lands on one of its
    // children, including client-side navigations after mount (the lazy
    // initializer above only runs once).
    useEffect(() => {
        setExpandedMenus(prev => {
            const toOpen = menuItems
                .filter(item =>
                    item.children?.some(
                        child => child.path === location.pathname,
                    ),
                )
                .map(item => item.name)
                .filter(name => !prev.includes(name));
            return toOpen.length ? [...prev, ...toOpen] : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleMenu = (name: string): void =>
        setExpandedMenus(prev =>
            prev.includes(name)
                ? prev.filter(open => open !== name)
                : [...prev, name],
        );

    const isParentActive = (item: MenuItem): boolean =>
        !!item.children?.some(child => child.path === location.pathname);

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
        const touch = e.touches[0];
        setDragState({
            isDragging: false, // Start as false, only set to true when actual dragging occurs
            startY: touch.clientY,
            currentY: touch.clientY,
            dragDistance: 0,
        });
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        const dragDistance = touch.clientY - dragState.startY;

        // Only start dragging if moved more than 10px vertically
        if (Math.abs(dragDistance) > 10 && dragDistance > 0) {
            setDragState((prev) => ({
                ...prev,
                isDragging: true,
                currentY: touch.clientY,
                dragDistance,
            }));
        }
    };

    const handleTouchEnd = () => {
        if (dragState.isDragging && dragState.dragDistance > 100) {
            setMobileOpen(false);
        }
        setDragState({
            isDragging: false,
            startY: 0,
            currentY: 0,
            dragDistance: 0,
        });
    };

    const getDragTransform = () => {
        if (!dragState.isDragging || dragState.dragDistance <= 0) return {};
        const progress = Math.min(dragState.dragDistance / 200, 1);
        return {
            transform: `translateY(${dragState.dragDistance * 0.5}px)`,
            opacity: 1 - progress * 0.3,
        };
    };

    if (isMobile) {
        return (
            <>
                {mobileOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-out"
                        onClick={() => setMobileOpen(false)}
                        style={{
                            opacity: dragState.isDragging
                                ? 0.5 -
                                  Math.min(dragState.dragDistance / 200, 1) *
                                      0.3
                                : 0.5,
                        }}
                    />
                )}
                <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-50">
                    <div
                        className="w-16 h-8 bg-primary rounded-t-full flex justify-center items-center cursor-pointer transition-all duration-200 hover:bg-primary/90 active:scale-95"
                        onClick={() => setMobileOpen(true)}
                    >
                        <BsChevronUp size={24} className="text-white" />
                    </div>
                </div>
                <div
                    className={`fixed bottom-0 left-0 w-full bg-gradient-to-t from-primary to-primary/95 backdrop-blur-sm text-white shadow-2xl z-50 transition-all duration-500 ease-out ${
                        mobileOpen && !dragState.isDragging
                            ? "transform translate-y-0 opacity-100"
                            : !mobileOpen
                            ? "transform translate-y-full opacity-0"
                            : ""
                    }`}
                    style={{
                        height: mobileOpen ? "70vh" : "0vh",
                        ...(dragState.isDragging ? getDragTransform() : {}),
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="flex flex-col h-full">
                        <div className="flex justify-center pt-4 pb-2 cursor-grab active:cursor-grabbing">
                            <div
                                className={`w-12 h-1 bg-white rounded-full mb-4 transition-all duration-200 ${
                                    dragState.isDragging
                                        ? "bg-white/70 w-16"
                                        : "bg-white/50"
                                }`}
                                onClick={() => setMobileOpen(false)}
                            />
                        </div>
                        <div className="flex flex-col space-y-2 px-6 pb-8 overflow-y-auto">
                            {filteredMenuItems.map((item, index) => {
                                const active = item.path
                                    ? location.pathname === item.path
                                    : isParentActive(item);
                                const isOpen = expandedMenus.includes(
                                    item.name,
                                );

                                return (
                                    <div key={index} className="flex flex-col">
                                        <div
                                            onClick={() => {
                                                if (dragState.isDragging) return;
                                                if (item.children) {
                                                    toggleMenu(item.name);
                                                } else if (item.path) {
                                                    navigate(item.path);
                                                    setMobileOpen(false);
                                                }
                                            }}
                                            className={`group flex items-center cursor-pointer p-4 rounded-xl transition-all duration-300 ease-out hover:bg-white/10 active:scale-95 ${
                                                active
                                                    ? "bg-white/20 text-secondary shadow-lg"
                                                    : "text-white hover:translate-x-1"
                                            } ${
                                                dragState.isDragging
                                                    ? "pointer-events-none"
                                                    : ""
                                            }`}
                                            style={{
                                                animationDelay:
                                                    mobileOpen &&
                                                    !dragState.isDragging
                                                        ? `${index * 50}ms`
                                                        : "0ms",
                                                animation:
                                                    mobileOpen &&
                                                    !dragState.isDragging
                                                        ? "slideInUp 0.4s ease-out forwards"
                                                        : "none",
                                            }}
                                        >
                                            <div className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
                                                {active ? (
                                                    <item.filledIcon size={24} />
                                                ) : (
                                                    <item.icon size={24} />
                                                )}
                                            </div>
                                            <span className="ml-4 text-base font-medium">
                                                {item.name}
                                            </span>
                                            {item.children && (
                                                <BsChevronDown
                                                    size={18}
                                                    className={`ml-auto transition-transform duration-300 ${
                                                        isOpen
                                                            ? "rotate-180"
                                                            : ""
                                                    }`}
                                                />
                                            )}
                                        </div>
                                        {item.children && isOpen && (
                                            <div className="flex flex-col mt-1 space-y-1">
                                                {item.children.map(
                                                    (child, childIndex) => {
                                                        const childActive =
                                                            location.pathname ===
                                                            child.path;
                                                        return (
                                                            <div
                                                                key={childIndex}
                                                                onClick={() => {
                                                                    if (
                                                                        dragState.isDragging
                                                                    )
                                                                        return;
                                                                    navigate(
                                                                        child.path,
                                                                    );
                                                                    setMobileOpen(
                                                                        false,
                                                                    );
                                                                }}
                                                                className={`flex items-center cursor-pointer py-3 pl-10 pr-4 rounded-xl transition-all duration-300 ease-out hover:bg-white/10 active:scale-95 ${
                                                                    childActive
                                                                        ? "bg-white/20 text-secondary"
                                                                        : "text-white hover:translate-x-1"
                                                                } ${
                                                                    dragState.isDragging
                                                                        ? "pointer-events-none"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <div className="flex-shrink-0">
                                                                    {childActive ? (
                                                                        <child.filledIcon
                                                                            size={
                                                                                20
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <child.icon
                                                                            size={
                                                                                20
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                                <span className="ml-3 text-sm font-medium">
                                                                    {child.name}
                                                                </span>
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Desktop sidebar: reverted to original implementation
    return (
        <div
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
            className={`
                bg-primary
                text-white 
                transition-all 
                duration-500 
                ease-in-out
                overflow-hidden
                ${
                    isMobile
                        ? "fixed bottom-0 left-0 w-full h-20"
                        : `h-full ${isExpanded ? "w-64" : "w-16"}`
                }
                z-50
            `}
        >
            <div
                className={`
                    flex ${isMobile ? "flex-row" : "flex-col"}
                    ${isMobile ? "justify-around" : "justify-between"}
                    ${isMobile ? "items-center" : "items-start"}
                    h-full p-2
                `}
            >
                <div
                    className={`
                        flex ${isMobile ? "flex-row w-full" : "flex-col"}
                        ${isMobile ? "justify-around" : "items-start"}
                        ${isMobile ? "" : "space-y-8"}
                        ${
                            isMobile
                                ? ""
                                : "flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1"
                        }
                        w-full
                    `}
                >
                    {filteredMenuItems.map((item, index) => {
                        const active = item.path
                            ? location.pathname === item.path
                            : isParentActive(item);
                        const isOpen = expandedMenus.includes(item.name);

                        return (
                            <div key={index} className="w-full">
                                <div
                                    className={`
                                        flex flex-row items-center cursor-pointer
                                        hover:bg-[#F0DFAF] ${
                                            active
                                                ? "text-secondary"
                                                : "text-white"
                                        } hover:text-primary p-2 rounded-md transition-colors duration-300 ease-in-out w-full
                                        ${!isExpanded ? "justify-center" : ""}
                                    `}
                                    onClick={() => {
                                        if (item.children) {
                                            // Collapsed rail hides the submenu, so
                                            // a click there should open the rail and
                                            // reveal it rather than do nothing.
                                            if (!isExpanded) {
                                                setIsExpanded(true);
                                                setExpandedMenus(prev =>
                                                    prev.includes(item.name)
                                                        ? prev
                                                        : [...prev, item.name],
                                                );
                                            } else {
                                                toggleMenu(item.name);
                                            }
                                        } else if (item.path) {
                                            navigate(item.path);
                                        }
                                    }}
                                >
                                    <div className="flex-shrink-0">
                                        {active ? (
                                            <item.filledIcon
                                                size={24}
                                                className="transition-transform duration-300 ease-in-out text-current"
                                            />
                                        ) : (
                                            <item.icon
                                                size={24}
                                                className="transition-transform duration-300 ease-in-out text-current"
                                            />
                                        )}
                                    </div>
                                    {isExpanded && (
                                        <>
                                            <span className="text-lg whitespace-nowrap transition-opacity duration-300 ease-in-out opacity-100 delay-300 ml-4">
                                                {item.name}
                                            </span>
                                            {item.children && (
                                                <BsChevronDown
                                                    size={16}
                                                    className={`ml-auto transition-transform duration-300 ${
                                                        isOpen
                                                            ? "rotate-180"
                                                            : ""
                                                    }`}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                {item.children && isExpanded && isOpen && (
                                    <div className="flex flex-col mt-2 space-y-2 w-full">
                                        {item.children.map(
                                            (child, childIndex) => {
                                                const childActive =
                                                    location.pathname ===
                                                    child.path;
                                                return (
                                                    <div
                                                        key={childIndex}
                                                        className={`
                                                            flex flex-row items-center cursor-pointer
                                                            hover:bg-[#F0DFAF] ${
                                                                childActive
                                                                    ? "text-secondary"
                                                                    : "text-white"
                                                            } hover:text-primary py-1.5 pl-4 pr-2 rounded-md transition-colors duration-300 ease-in-out w-full
                                                        `}
                                                        onClick={() =>
                                                            navigate(child.path)
                                                        }
                                                    >
                                                        <div className="flex-shrink-0">
                                                            {childActive ? (
                                                                <child.filledIcon
                                                                    size={20}
                                                                    className="text-current"
                                                                />
                                                            ) : (
                                                                <child.icon
                                                                    size={20}
                                                                    className="text-current"
                                                                />
                                                            )}
                                                        </div>
                                                        <span className="text-base whitespace-nowrap ml-3">
                                                            {child.name}
                                                        </span>
                                                    </div>
                                                );
                                            },
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AdminSideBar;
